import { ReactElement, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { HistoryIcon } from '@twilio-paste/icons/esm/HistoryIcon';
import { NotesIcon } from '@twilio-paste/icons/esm/NotesIcon';
import { SMSIcon } from '@twilio-paste/icons/esm/SMSIcon';
import { ArrowBackIcon } from '@twilio-paste/icons/esm/ArrowBackIcon';

import { WiseOwl } from '@twiliointernal/wise-owl';
import {
  WiseOwlIngress,
  OnErrorResponse,
  OnEventResponse,
  OnTrackEventResponse,
  WiseOwlBaseProps,
  WiseOwlEventEnum,
  WiseOwlUser,
  WiseOwlApiIngress,
  WiseOwlElementIds,
  liveChatCopy,
  wiseOwlViewCopy,
  OSC_WO_DISPLAY_NAME,
} from '@twiliointernal/e2-commons-shared-definitions';
import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';

import { constants } from './definitions';
import { useUserDetails, useToasterContext } from '../../hooks';
import { logger, Segment } from '@twiliointernal/e2-commons-shared-libs';
import { loginRedirectToOsc } from '../../utils';

const { WISE_OWL_CHAT_CONTAINER } = WiseOwlElementIds;

const WiseOwlView = (): ReactElement => {
  const toaster = useToasterContext();
  const navigate = useNavigate();
  const { chatId: chatIdFromRoute } = useParams<'chatId'>() as {
    chatId?: string;
  };
  const { account, user, isLoading, isLoggedIn } = useUserDetails();
  const { trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoggedIn,
    isLoading,
  });

  useEffect(() => {
    /**
     * User is not logged in and chatId is present in route => redirect
     * This is because we match a chatId by a userId in the WO API, which
     * cannot be done for ungated users because the stargateUngatedUserId
     * can be inconsistent.
     */
    if (!isLoggedIn && chatIdFromRoute) {
      navigate('/');
    }
  }, [isLoggedIn]);

  const handleEvent = ({ eventType, data }: OnEventResponse): void => {
    const logMessage = constants.logMessageToEventTypeMap[eventType];

    // Log mapped events
    if (logMessage) {
      logger.info(logMessage, data);
    }

    switch (eventType) {
      // Outlier events
      case WiseOwlEventEnum.startNewChat: {
        navigate(`/chat/${data.chatId}`);
        break;
      }
      case WiseOwlEventEnum.loginRedirect: {
        loginRedirectToOsc();
        break;
      }
      case WiseOwlEventEnum.redirect: {
        // TODO: This is a temporary workaround to keep from having a bad URL to the pricing and plans page.
        // We need to get to the root cause here.
        if (
          data.redirectUrl &&
          data.redirectUrl === 'https://sendgrid.com/en-us/pricing-and-plans'
        ) {
          navigate('/support-plans');
        } else {
          navigate(`/${data.redirectUrl}`);
        }
        break;
      }
      case WiseOwlEventEnum.getChat: {
        /**
         * If "resumeChat" is "true",
         * it means the chat was interrupted
         * and we need to clear the interrupted
         * chat id from session storage and
         * navigate to the chat.
         */
        if (data.resumeChat) {
          navigate(`/chat/${data.chatId}`);
        }
        break;
      }

      // Warn logs
      case WiseOwlEventEnum.sendChatMessageRetry ||
        WiseOwlEventEnum.startNewChatRetry: {
        const logMessage = constants.logMessageToEventTypeMap[eventType];

        logger.warn(logMessage, data);
        break;
      }
    }
  };

  const handleError = ({ eventType, data }: OnErrorResponse): void => {
    // Log all errors
    const logMessage = constants.errorLogMessageToEventTypeMap[eventType];
    logger.error(logMessage, data);

    // Outlier events
    switch (eventType) {
      case WiseOwlEventEnum.getChat: {
        navigate('/');
        break;
      }
      case WiseOwlEventEnum.liveChatTransfer: {
        toaster.push({
          message: wiseOwlViewCopy.errors.liveChatTransfer,
          variant: 'error',
          dismissAfter: 5000,
        });
        break;
      }
    }
  };

  const handleTrackEvent = ({
    eventName,
    additionalData = {},
  }: OnTrackEventResponse): void => {
    trackEvent(eventName, {
      user,
      ingress: WiseOwlApiIngress.OneSupportCenter,
      ...additionalData,
    });
  };

  const stargateUngatedUserId = Segment.getAnonymousUserId();
  const wiseOwlUser: WiseOwlUser = {
    firstName: user ? user.firstName : 'M',
    lastName: user ? user.lastName : 'E',
    accountSid: account?.sid,
  };

  const customStyles = `
    display: flex;
    flex-direction: column;
    height: ${
      isLoggedIn
        ? constants.WISE_OWL_GATED_CHAT_HEIGHT
        : constants.WISE_OWL_UNGATED_CHAT_HEIGHT
    };
    margin: 2rem auto 0;
    max-width: 44.5rem;
    min-height: ${constants.WISE_OWL_CHAT_MIN_HEIGHT};

    div[data-paste-element='${WISE_OWL_CHAT_CONTAINER}'] {
      overflow-y: hidden;
    }
  `;

  const ingressLiveChatPreEngagementData = {
    friendlyName: `${user?.firstName} ${user?.lastName}`,
    email: user?.emailAddr,
    userSid: user?.sid,
    accountSid: user?.currentAccountSid,
    identity: user?.emailAddr,
  };

  const defaultWiseOwlProps: WiseOwlBaseProps = {
    displayName: OSC_WO_DISPLAY_NAME,
    user: wiseOwlUser,
    chatId: chatIdFromRoute,
    liveChatConfiguration: {
      waitingActions: {
        ticketSubmission: {
          label: liveChatCopy.waitingActions.submitTicket,
          icon: <NotesIcon color={'colorTextIcon'} decorative />,
        },
        requestNotification: {
          label: liveChatCopy.waitingActions.receiveText,
          icon: <SMSIcon color={'colorTextIcon'} decorative />,
        },
      },
      endedActions: {
        navigateHome: {
          label: liveChatCopy.endedActions.home,
          icon: <ArrowBackIcon decorative />,
          handleActionButtonClick: () => navigate('/'),
        },
        navigateTicketDetails: {
          label: liveChatCopy.endedActions.ticketDetails,
          icon: <HistoryIcon decorative />,
          handleActionButtonClick: (ticketId: number) =>
            navigate(`/tickets/${ticketId}`),
        },
      },
    },
    onError: handleError,
    onEvent: handleEvent,
    onTrackEvent: handleTrackEvent,
    surveyId: constants.SURVEY_ID,
    ingressLiveChatPreEngagementData,
    customStyles,
  };

  return isLoggedIn ? (
    <WiseOwl ingress={WiseOwlIngress.osc} {...defaultWiseOwlProps} />
  ) : (
    <WiseOwl
      ingress={WiseOwlIngress.oscUngated}
      stargateUngatedUserId={stargateUngatedUserId}
      {...defaultWiseOwlProps}
    />
  );
};

export default WiseOwlView;
